/**
 * Shared pure helpers for deriving Expense Inventory list bucket/status values.
 * @module src/util/expenseInventory
 */
import {
  EXPENSE_INVENTORY_OPS_PERSONAS,
  EXPENSE_INVENTORY_SALES_REP_PERSONAS,
  EXPENSE_INVENTORY_SCREEN_TYPE,
  EXPENSE_INVENTORY_USER_ROLE,
  PERSONA_VALUE_SEPARATOR,
  Persona,
} from './constants';

export type ExpenseInventoryUserRole = typeof EXPENSE_INVENTORY_USER_ROLE[keyof typeof EXPENSE_INVENTORY_USER_ROLE];

const normalizePersona = (persona: string): string => persona.trim().toLowerCase();

const NORMALIZED_OPS_PERSONAS = EXPENSE_INVENTORY_OPS_PERSONAS.map(normalizePersona);
const NORMALIZED_SALES_REP_PERSONAS = EXPENSE_INVENTORY_SALES_REP_PERSONAS.map(normalizePersona);
const NORMALIZED_SALES_ASSOCIATE = normalizePersona(Persona.SALES_ASSOCIATE);
const NORMALIZED_SALES_MANAGER = normalizePersona(Persona.SALES_MANAGER);

/**
 * Which Expense Inventory view the logged-in user gets, from their Salesforce persona value.
 * The value can hold several personas joined by ';' (e.g. 'Sales Associate;MPS' or
 * 'Business Admin;CSR;Branch Op;'), so each one is compared on its own, trimmed and
 * case-insensitive.
 *
 * The order follows the backend's resolvePersonaScopeFlags, which decides who may review:
 * an Ops persona first, then ASR, then RSM. So a Sales Manager who is also a Sales Associate
 * is an ASR (the backend refuses them as reviewers), and any other Sales Manager is Ops.
 */
export const getExpenseInventoryUserRole = (personas?: string | null): ExpenseInventoryUserRole => {
  // An empty persona, as after the trailing ';' in 'Business Admin;CSR;Branch Op;', matches nothing.
  const userPersonas = (personas ?? '').split(PERSONA_VALUE_SEPARATOR).map(normalizePersona);
  const isSalesManagerReviewer =
    userPersonas.includes(NORMALIZED_SALES_MANAGER) &&
    !userPersonas.includes(NORMALIZED_SALES_ASSOCIATE);

  if (userPersonas.some(persona => NORMALIZED_OPS_PERSONAS.includes(persona))) {
    return EXPENSE_INVENTORY_USER_ROLE.OPS;
  }
  if (isSalesManagerReviewer) {
    return EXPENSE_INVENTORY_USER_ROLE.OPS;
  }
  if (userPersonas.some(persona => NORMALIZED_SALES_REP_PERSONAS.includes(persona))) {
    return EXPENSE_INVENTORY_USER_ROLE.SALES_REP;
  }
  return EXPENSE_INVENTORY_USER_ROLE.NONE;
};

/** Derives the active Expense Inventory bucket from a route pathname. */
export const statusFromPathname = (pathname: string): string => {
  const lower = pathname.toLowerCase();
  if (lower.includes('/inventory/expense/failed')) return EXPENSE_INVENTORY_SCREEN_TYPE.FAILED;
  if (lower.includes('/inventory/expense/processed'))
    return EXPENSE_INVENTORY_SCREEN_TYPE.PROCESSED;
  return EXPENSE_INVENTORY_SCREEN_TYPE.OPEN;
};

/** Maps an Expense Inventory bucket to its corresponding tab index. */
export const tabIndexForStatus = (status: string): number => {
  if (status === EXPENSE_INVENTORY_SCREEN_TYPE.FAILED) return 1;
  if (status === EXPENSE_INVENTORY_SCREEN_TYPE.PROCESSED) return 2;
  return 0;
};

/** Maps an Expense Inventory bucket to its display label. */
export const ACTIVE_TYPE_LABEL_BY_STATUS: Record<string, string> = {
  [EXPENSE_INVENTORY_SCREEN_TYPE.FAILED]: 'Failed',
  [EXPENSE_INVENTORY_SCREEN_TYPE.PROCESSED]: 'Processed',
  [EXPENSE_INVENTORY_SCREEN_TYPE.OPEN]: 'Open',
};

/** The detail fields an Expense Inventory submit or Ops review sends back as its header. */
export interface ExpenseInventoryHeaderSource {
  salesRepId?: string | null;
  writeOffType?: string | null;
  accountId?: string | null;
  shipTo?: string | null;
  usageDate?: string | null;
  usedInPatientCare?: boolean | null;
  marketingCollaboration?: boolean | null;
  doctorName?: string | null;
  facilityNameOrLocation?: string | null;
}

/** The header argument of submitExpenseInventory and reviewExpenseInventory. */
export interface ExpenseInventorySubmitHeader {
  salesRepId: string;
  writeOffType: string;
  accountId: string | null;
  shipToId: string | null;
  usageDate: string | null;
  usedInPatientCare: boolean;
  marketingCollaboration: boolean;
  doctorName: string | null;
  facilityNameOrLocation: string | null;
}

/**
 * The header as this screen shows it, named as createExpenseInventory names its arguments. The
 * detail's shipTo is the Ship To account id, the value the edit form sends as shipToId.
 */
export const buildExpenseInventorySubmitHeader = (
  detail?: ExpenseInventoryHeaderSource | null
): ExpenseInventorySubmitHeader => ({
  salesRepId: detail?.salesRepId ?? '',
  writeOffType: detail?.writeOffType ?? '',
  accountId: detail?.accountId ?? null,
  shipToId: detail?.shipTo ?? null,
  usageDate: detail?.usageDate ?? null,
  usedInPatientCare: !!detail?.usedInPatientCare,
  marketingCollaboration: !!detail?.marketingCollaboration,
  doctorName: detail?.doctorName ?? null,
  facilityNameOrLocation: detail?.facilityNameOrLocation ?? null,
});
